import React, { Component } from 'react';
import { Button, Collapse, Flex, Heading, Icon, Spinner, Text } from 'core/components';
import { inject, observer } from 'mobx-react';
import { showErrorToast } from 'core/components/toast';
import { TbLayoutSidebar } from 'react-icons/tb';
import {
  SERVER_PAUSE_KINDS,
  AGENT_TURN_STATUS_TYPES,
  TOOL_CALL_STATUS,
  TOOL_INTERRUPTED_TEXT,
  TOOL_NOT_RUN_REASON,
  TOOL_PAUSE_KIND,
  isMcpTool
} from '@kentik/ui-shared/kaizen/constants';
import { isDeviceSpecificErrorTool, isStoppedToolCall } from './util/toolErrorUtils';
import { ToolCallCard, ToolCallError } from './KaizenChatSession.styles';
import { createToolResultComponent, hasToolResultView, isSubAgentTool, TOOL_NAMES } from './ToolResultFactory';
import ToolDebugResult from './toolResults/ToolDebugResult';
import { KaizenToolResultConfirmActions } from './KaizenToolResultConfirmActions';
import KaizenToolResultConnectActions from './KaizenToolResultConnectActions';
import McpServerLabel from './components/McpServerLabel';
import KaizenErrorMessage from './components/KaizenErrorMessage';
import { hasToolResultData, isShowingApprovalPreview } from './util/toolResultAvailability';

const SEND_TO_EXPLORER_TOOLS = [TOOL_NAMES.DATA_EXPLORER, TOOL_NAMES.SNMP_TRAP, TOOL_NAMES.NMS];

const SERVER_ROW_RAN_STATUSES = [
  TOOL_CALL_STATUS.EXECUTING,
  TOOL_CALL_STATUS.SUCCESS,
  TOOL_CALL_STATUS.FAILED,
  TOOL_CALL_STATUS.TIMED_OUT
];

const NOT_RUN_TEXT = {
  [TOOL_NOT_RUN_REASON.SKIPPED]: (serverName) => `Not called: ${serverName} was declined for this request.`,
  [TOOL_NOT_RUN_REASON.ACCESS_LOST]: (serverName) =>
    `Not called: access to ${serverName} was lost earlier in this request.`,
  [TOOL_NOT_RUN_REASON.UNAVAILABLE]: (serverName) => `Not called: ${serverName} is temporarily unavailable.`,
  [TOOL_NOT_RUN_REASON.CHANGED]: (serverName) => `Not called: ${serverName} changed while this request was waiting.`,
  [TOOL_NOT_RUN_REASON.NO_LONGER_AVAILABLE]: (serverName) => `Not called: ${serverName} is no longer available to you.`,
  [TOOL_NOT_RUN_REASON.NO_TOOLS]: (serverName) => `Not called: ${serverName} offers no tools to your account.`
};

// Tool calls still showing a proposed-input preview (e.g. a diff) can open the detail view before they run.
function toolHasApprovalPreview(tool) {
  return hasToolResultView(tool.tool_name) && isShowingApprovalPreview(tool);
}

// Tools that delegate work to a dedicated sub-agent. While these are pending, the main
// agent is waiting for the sub-agent to run its own tools and respond.
const SUBAGENT_TOOL_DISPLAY_NAMES = {
  [TOOL_NAMES.MATH_AGENT]: 'Math Agent',
  [TOOL_NAMES.SYNTHETICS_MONITORING_AGENT]: 'Synthetics Agent'
};

@inject('$auth')
@observer
class KaizenToolResult extends Component {
  _flashTimeout = null;

  state = {
    shouldFlash: false,
    isExpanded: false,
    isApprovingTool: false
  };

  componentDidUpdate(prevProps, prevState) {
    const { tool, sessionModel } = this.props;
    const prevStatus = prevProps.tool?.status;
    const currStatus = tool?.status;
    const prevAwaitingApproval = prevProps.tool?.awaitingApproval;
    const currAwaitingApproval = tool?.awaitingApproval;

    // Flash logic
    if (
      (!prevStatus || prevStatus === 'PROCESSING') &&
      currStatus === TOOL_CALL_STATUS.SUCCESS &&
      !prevState.shouldFlash
    ) {
      clearTimeout(this._flashTimeout);
      setTimeout(() => {
        this.setState({ shouldFlash: true });
        this._flashTimeout = setTimeout(() => {
          this.setState({ shouldFlash: false });
        }, 900);
      }, 0);
    }

    // Auto-approval check when status changes to awaiting approval
    const isNowAwaitingApproval = currStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || currAwaitingApproval;
    const wasAwaitingApproval = prevStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || prevAwaitingApproval;

    if (isNowAwaitingApproval && !wasAwaitingApproval) {
      if (tool && sessionModel && !this.isServerRow && this.isAutoApproved(sessionModel.id, tool.tool_name)) {
        // Auto-approve after a small delay to ensure the UI has updated
        setTimeout(() => {
          this.handleApprove(false);
        }, 100);
      } else if (!this.isInline && tool?.query_object?._hasApprovalPreview) {
        // Auto-open the side panel so the user sees the preview without having to click
        const { onSelectToolResult } = this.props;
        onSelectToolResult?.(tool);
      }
    }
  }

  componentDidMount() {
    const { tool, sessionModel, onSelectToolResult } = this.props;
    const isAwaitingApproval = tool?.status === TOOL_CALL_STATUS.AWAITING_APPROVAL || tool?.awaitingApproval;

    if (
      isAwaitingApproval &&
      tool &&
      sessionModel &&
      !this.isServerRow &&
      this.isAutoApproved(sessionModel.id, tool.tool_name)
    ) {
      this.handleApprove(false);
    } else if (isAwaitingApproval && !this.isInline && tool?.query_object?._hasApprovalPreview) {
      // On reload the tool may already be awaiting approval — open the panel immediately
      onSelectToolResult?.(tool);
    }
  }

  componentWillUnmount() {
    clearTimeout(this._flashTimeout);
  }

  // Cookie helper functions
  setAutoApprovalCookie(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const existingValue = this.getCookie(cookieName);
    const toolNames = existingValue ? existingValue.split(',') : [];

    if (!toolNames.includes(toolName)) {
      toolNames.push(toolName);
    }

    // Set cookie to expire at end of session
    document.cookie = `${cookieName}=${toolNames.join(',')};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  isAutoApproved(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const value = this.getCookie(cookieName);
    if (value) {
      const toolNames = value.split(',');
      return toolNames.includes(toolName);
    }
    return false;
  }

  handleToggleExpanded = () => {
    this.setState((prevState) => ({
      isExpanded: !prevState.isExpanded
    }));
  };

  handleSendToExplorer = () => {
    const { tool, onSendToExplorer } = this.props;
    if (onSendToExplorer && tool.query_object) {
      onSendToExplorer(tool.query_object, tool.raw_result);
    }
  };

  get isInline() {
    const { inline, isCopilotMode } = this.props;
    return Boolean(inline || isCopilotMode);
  }

  get isConnectRow() {
    const { tool } = this.props;
    return tool?.pause_context?.kind === TOOL_PAUSE_KIND.CONNECT;
  }

  get isServerRow() {
    const { tool } = this.props;
    return SERVER_PAUSE_KINDS.includes(tool?.pause_context?.kind);
  }

  get hasDebugPayload() {
    const { tool } = this.props;
    return Boolean(tool.query_object || tool.raw_result || tool.processed_result || tool.error_message);
  }

  handleClick = () => {
    const { tool, onSelectToolResult, onSendToExplorer } = this.props;
    const hasDetailView = hasToolResultView(tool.tool_name) && hasToolResultData(tool);
    const canSendToExplorer = onSendToExplorer && SEND_TO_EXPLORER_TOOLS.includes(tool.tool_name);

    // When canSendToExplorer is true, card click does nothing — use the button
    if (canSendToExplorer) {
      return;
    }

    if (this.isInline) {
      // Inline surfaces (copilot, sub-agent transcript) expand the result in place
      this.handleToggleExpanded();
    } else if (hasDetailView || toolHasApprovalPreview(tool)) {
      onSelectToolResult(tool);
    }
  };

  sendDecision = (approved) => {
    const { tool, sessionModel } = this.props;
    this.setState({ isApprovingTool: true });
    sessionModel
      .approveToolCall(tool.tool_tracking_id, approved, tool.agent_step_id)
      .then(() => {
        this.setState({ isApprovingTool: false });
      })
      .catch((err) => {
        console.error('Failed to send tool decision:', err);
        this.setState({ isApprovingTool: false });
      });
  };

  handleCancel = (e) => {
    e?.stopPropagation();
    const { sessionModel, turn } = this.props;
    return sessionModel.stopTurn(turn.id).catch(() => showErrorToast('Could not cancel the turn. Try again.'));
  };

  handleApprove = (alwaysApprove = false) => {
    const { tool, sessionModel, $auth } = this.props;

    if (alwaysApprove) {
      this.setAutoApprovalCookie(sessionModel.id, tool.tool_name);
    }

    const { chfAdmin, user_name: userName, user_full_name: userFullName, id } = $auth.activeUser || {};
    tool.approved_by_user_name = userFullName || userName || 'Unknown User';
    if (chfAdmin && chfAdmin.id !== id) {
      tool.approved_by_spoof_user_name = chfAdmin.user_full_name || chfAdmin.user_name;
    }

    this.sendDecision(true);
  };

  // Stop propagation so approving/rejecting doesn't also toggle the now-clickable card's panel.
  handleApproveClick = (e) => {
    e?.stopPropagation();
    this.handleApprove(false);
  };

  handleApproveAndAutoApproveClick = (e) => {
    e?.stopPropagation();
    this.handleApprove(true);
  };

  handleRejectClick = (e) => {
    e?.stopPropagation();
    this.handleReject();
  };

  handleReject = () => {
    this.sendDecision(false);
  };

  handleContinue = () => {
    this.sendDecision(true);
  };

  getDetailViewButtonText = (isExpanded) => {
    if (this.isInline) {
      return isExpanded ? 'Hide details' : 'View details';
    }
    return 'View details';
  };

  getDetailViewButtonIcon = (isExpanded) => {
    if (this.isInline) {
      return isExpanded ? 'caret-up' : 'caret-down';
    }
    return TbLayoutSidebar;
  };

  renderDetailViewButton = (canSendToExplorer, isExpanded) => {
    if (canSendToExplorer) {
      return (
        <Button
          small
          intent="primary"
          text="Send to Data Explorer"
          icon="arrow-right"
          onClick={(e) => {
            e.stopPropagation();
            this.handleSendToExplorer();
          }}
        />
      );
    }

    return (
      <>
        <Text muted small>
          {this.getDetailViewButtonText(isExpanded)}
        </Text>
        <Icon icon={this.getDetailViewButtonIcon(isExpanded)} size={14} color="muted" />
      </>
    );
  };

  render() {
    const { tool, turn, isAsker, askerName, onDataViewCreate, onQueryError, sessionModel, onSendToExplorer } =
      this.props;
    const { shouldFlash, isExpanded, isApprovingTool } = this.state;

    if (!tool) {
      return null;
    }

    const { title, status, awaitingApproval } = tool;
    const hasDetailView = hasToolResultView(tool.tool_name) && hasToolResultData(tool);
    const isSuccess = status === TOOL_CALL_STATUS.SUCCESS;
    const canSendToExplorer = onSendToExplorer && SEND_TO_EXPLORER_TOOLS.includes(tool.tool_name) && isSuccess;

    // Tools without a dedicated view still expand inline, showing their raw input/output.
    const showDetailInline = hasDetailView && isSuccess;
    const inlineExpandable = this.isInline && !canSendToExplorer && (showDetailInline || this.hasDebugPayload);

    // A sub-agent streams its steps into the panel, so it's worth opening before it finishes.
    // Also allow opening during AWAITING_APPROVAL when the tool provides an approval preview.
    const hasApprovalPreview = toolHasApprovalPreview(tool);
    const canOpenPanel =
      !this.isInline &&
      ((hasDetailView &&
        (isSuccess || isSubAgentTool(tool.tool_name)) &&
        (!this.isServerRow || SERVER_ROW_RAN_STATUSES.includes(status))) ||
        hasApprovalPreview);

    const isInteractive = canOpenPanel || canSendToExplorer || inlineExpandable;
    const notRunText =
      NOT_RUN_TEXT[tool.raw_result?.not_run]?.(tool.raw_result.server_name) ||
      (Object.values(TOOL_INTERRUPTED_TEXT).includes(tool.error_message) ? tool.error_message : null);
    // a tool the user's stop ended, or one closed with its cancelled turn
    const isStopped = isStoppedToolCall(tool) || status === TOOL_CALL_STATUS.CANCELLED;
    const isFailed =
      !notRunText &&
      !isStopped &&
      (status === TOOL_CALL_STATUS.FAILED || (this.isServerRow && status === TOOL_CALL_STATUS.TIMED_OUT));
    const isRejected = status === TOOL_CALL_STATUS.USER_REJECTED;
    const isTimeout = !notRunText && status === TOOL_CALL_STATUS.APPROVAL_TIMEOUT;
    const isExecuting = status === TOOL_CALL_STATUS.EXECUTING;
    const isAwaitingApproval = status === TOOL_CALL_STATUS.AWAITING_APPROVAL || awaitingApproval;
    const { isConnectRow } = this;
    const isAutoApproved = !this.isServerRow && sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name);
    const shouldShowApprovalUI = isAwaitingApproval && !isAutoApproved && !isConnectRow;
    const isGrantLost = isMcpTool(tool.tool_name) && tool.raw_result?.grant_lost === true;

    // When a sub-agent delegation tool is still running, surface that the main agent is
    // waiting on the sub-agent rather than leaving a bare spinner + title.
    const subAgentDisplayName = SUBAGENT_TOOL_DISPLAY_NAMES[tool.tool_name];
    const isPending = !status || status === TOOL_CALL_STATUS.PENDING || status === 'PROCESSING';
    const isWaitingForSubAgent = Boolean(subAgentDisplayName) && isPending && !shouldShowApprovalUI;

    const spoofApproverName =
      tool.approved_by_spoof_user_name ||
      tool.approvedBySpoofUser?.user_full_name ||
      tool.approvedBySpoofUser?.user_name;
    const targetApproverName =
      tool.approved_by_user_name || tool.approvedByUser?.user_full_name || tool.approvedByUser?.user_name;
    const approverName = spoofApproverName ? `[Kentik] ${spoofApproverName}` : targetApproverName;
    const showApprovedBy =
      !isConnectRow &&
      Boolean(approverName) &&
      [TOOL_CALL_STATUS.SUCCESS, TOOL_CALL_STATUS.USER_APPROVED, TOOL_CALL_STATUS.EXECUTING].includes(status);

    const headerProps = {
      color: !status ? 'muted' : 'body',
      fontWeight: isInteractive ? 500 : 300
    };

    let cardClass = status ? status.toLowerCase() : '';
    if (status === TOOL_CALL_STATUS.SUCCESS && shouldFlash) {
      cardClass += ' flash';
    }

    if (isExpanded) {
      cardClass += ' expanded';
    }

    const toolResultCard = (
      <ToolCallCard
        interactive={isInteractive}
        elevation={isInteractive ? 2 : 0}
        className={cardClass}
        onClick={isInteractive ? this.handleClick : null}
      >
        <Flex alignItems="center" justifyContent="space-between" gap={2}>
          <Flex alignItems="center" gap={1} flex={1} minWidth={0}>
            {(!status || isExecuting) && <Spinner size={12} variant={isWaitingForSubAgent ? 'hourglass' : undefined} />}
            {isFailed && <Icon icon="error" color="danger" size={16} />}
            {notRunText && <Icon icon="disable" color="muted" size={16} />}
            {isRejected && <Icon icon="error" size={16} />}
            {isTimeout && <Icon icon="time" color="muted" size={16} />}
            {isStopped && <Icon icon="stop" color="muted" size={16} />}
            {isAwaitingApproval && !isAutoApproved && <Icon icon="pause" color="muted" size={16} />}
            {(status === TOOL_CALL_STATUS.SUCCESS || (isAwaitingApproval && isAutoApproved)) && (
              <Icon icon="tick-circle" color="success" className="success-icon" size={16} />
            )}
            <Heading level={6} m={0} {...headerProps}>
              {title}
            </Heading>
            {isMcpTool(tool.tool_name) && <McpServerLabel toolName={tool.tool_name} />}
          </Flex>

          {isStopped || ((isRejected || isTimeout) && !isConnectRow) ? (
            <Flex alignItems="center" gap={2} flexShrink={0}>
              {isStopped && (
                <Text small muted>
                  Stopped
                </Text>
              )}
              {isRejected && !isConnectRow && <Text fontWeight="bold">Rejected: Tool execution was not approved</Text>}
              {isTimeout && !isConnectRow && <Text fontWeight="bold">Timeout: Approval request timed out</Text>}
              {(canSendToExplorer || inlineExpandable || canOpenPanel) && (
                <Flex alignItems="center" gap="6px">
                  {this.renderDetailViewButton(canSendToExplorer, isExpanded)}
                </Flex>
              )}
            </Flex>
          ) : (
            (canSendToExplorer || inlineExpandable || canOpenPanel) && (
              <Flex alignItems="center" gap="6px" flexShrink={0}>
                {this.renderDetailViewButton(canSendToExplorer, isExpanded)}
              </Flex>
            )
          )}
        </Flex>

        {isFailed && (
          <ToolCallError as="div" small color="danger" className="tool-call-error">
            <Text fontWeight="bold">Error:</Text>{' '}
            <KaizenErrorMessage message={tool.error_message} showMessage={isDeviceSpecificErrorTool(tool.tool_name)} />
          </ToolCallError>
        )}

        {notRunText && (
          <Text as="div" small muted mt={1}>
            {notRunText}
          </Text>
        )}

        {isWaitingForSubAgent && (
          <Flex alignItems="center" gap="6px" mt="2px" ml="24px">
            <Text small color="muted" fontStyle="italic">
              Waiting for {subAgentDisplayName} to respond…
            </Text>
          </Flex>
        )}

        {isConnectRow && (
          <KaizenToolResultConnectActions
            tool={tool}
            turn={turn}
            sessionId={sessionModel?.id}
            isAsker={isAsker}
            askerName={askerName}
            onContinue={this.handleContinue}
            onSkip={this.handleReject}
            onCancel={
              turn?.processing_status === AGENT_TURN_STATUS_TYPES.AWAITING_APPROVAL ? this.handleCancel : undefined
            }
            disabled={isApprovingTool}
          />
        )}

        {isGrantLost && (
          <Text small color="warning" mt={1}>
            Access to {tool.raw_result.server_name} was lost during this request. Some results may be missing.
          </Text>
        )}

        {shouldShowApprovalUI && (
          <KaizenToolResultConfirmActions
            tool={tool}
            isAsker={isAsker}
            askerName={askerName}
            onApprove={this.handleApproveClick}
            onApproveAndAutoApprove={this.handleApproveAndAutoApproveClick}
            onReject={this.handleRejectClick}
            onCancel={
              turn?.processing_status === AGENT_TURN_STATUS_TYPES.AWAITING_APPROVAL ? this.handleCancel : undefined
            }
            disabled={isApprovingTool}
            hasApprovalPreview={hasApprovalPreview}
          />
        )}

        {showApprovedBy && (
          <Flex mt={1} alignItems="center" gap={1}>
            <Icon icon="tick" size={12} color="success" />
            <Text small color="muted">
              Approved by <strong>{approverName}</strong>
            </Text>
          </Flex>
        )}
      </ToolCallCard>
    );

    // Inline surfaces expand the result beneath the card (but NOT for Send-to-Explorer tools)
    if (inlineExpandable) {
      return (
        <Flex flexDirection="column" gap={0.5}>
          {toolResultCard}
          {isExpanded && (
            <Collapse isOpen={isExpanded}>
              {showDetailInline || hasApprovalPreview ? (
                createToolResultComponent(tool, {
                  onCloseToolResult: () => this.setState({ isExpanded: false }),
                  onDataViewCreate,
                  onQueryError,
                  isInlineMode: true
                })
              ) : (
                <ToolDebugResult
                  tool={tool}
                  onCloseToolResult={() => this.setState({ isExpanded: false })}
                  isInlineMode
                />
              )}
            </Collapse>
          )}
        </Flex>
      );
    }

    // In normal mode, just show the card
    return toolResultCard;
  }
}

export default KaizenToolResult;
